import { notification, Spin } from "antd";
import React, { useEffect, useState } from "react";
import {
  FormControl,
  TextField,
  Grid,
  InputLabel,
  Select,
  styled,
  OutlinedInput,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import { useLocation } from "react-router-dom";
import { executeIntent } from "../../../utils/IntentExecutor";
import IntentType from "../../../../intentexecutor/utils/IntentType";
import ErrorNotificationComponent from "../../../../components/generic/error/ErrorNotificationComponent";
import { MuiTelInput } from "mui-tel-input";
import { Subject } from "rxjs";
import { EventType } from "../../../../service/network/utils/EventType";
import { strings } from "../../../utils/strings";
import { statusCode } from "../../../utils/statuscodes";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";
import CustomModal from "../../../../components/generic/custommodal/CustomModal";

export const teamMemberApiResponse = new Subject();

const CustomOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--main-theme-color) !important",
    borderWidth: "1px !important",
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const AddEditTeamMemberModalComponent = ({ isModalOpen, setIsModalOpen }) => {
  const [teamMemberData, setTeamMemberData] = useState({
    telephone: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const userDetails = useReadMemory(MemoryType.USER);
  const query = new URLSearchParams(useLocation().search);
  const patientIdentifier = query.get("identifier");
  const [memberTypes, setMemberTypes] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectMemberType = (e) => {
    const { value } = e.target;
    setTeamMemberData({ ...teamMemberData, memberType: value });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTeamMemberData({ ...teamMemberData, [name]: value });
  };

  const handlePhoneNumberChange = (value) => {
    setTeamMemberData({ ...teamMemberData, telephone: value });
  };

  const validate = (values) => {
    const validatedErrors = {};
    const emailRegex = /^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/;
    if (!values.memberType) {
      validatedErrors.memberType = "Member Type is required!";
    }
    if (!values.givenName) {
      validatedErrors.givenName = "* First name is required!";
    }
    if (!values.familyName) {
      validatedErrors.familyName = "* Family name is required!";
    }
    if (!values.email) {
      validatedErrors.email = "* Email is required!";
    } else if (!emailRegex.test(values.email)) {
      validatedErrors.email = "* Enter valid email!";
    }
    return validatedErrors;
  };

  const addTeamMember = async () => {
    setLoading(true);
    const payload = {
      data: {
        givenName: teamMemberData?.givenName,
        familyName: teamMemberData?.familyName,
        email: teamMemberData?.email,
        organizationIdentifier: userDetails?.organizationIdentifier,
        jobTitle: "CareCircle",
        memberTitle: teamMemberData?.memberType,
        sendInvitation: false,
        patientIdentifier: patientIdentifier,
        workforceIdentifier: userDetails?.userIdentifier,
        telephone: teamMemberData?.telephone,
        creatorIdentifier: userDetails?.userIdentifier,
      },
    };

    const response = await executeIntent(IntentType.ADD_TEAM_MEMBER, payload);

    if (response?.code === statusCode.EMAIL_ALREADY_EXISTS) {
      setErrors({ email: strings.EMAIL_ALREADY_EXISTS });
    } else if (response?.status === 500) {
      ErrorNotificationComponent(new Error("Something went wrong"));
    } else {
      teamMemberApiResponse.next({
        type: EventType.PATIENT_TEAM_MEMBER_RESPONSE,
      });
      notification.success({
        message: strings.CARE_TEAM_MEMBER_ADDED_SUCCESS,
        placement: "bottomRight",
      });
    }

    setLoading(false);
    if (response?.code === statusCode.EMAIL_ALREADY_EXISTS) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  };

  const handleModalPrimaryButtonOnclick = (e) => {
    e.preventDefault();
    setErrors(validate(teamMemberData));
    setIsSubmit(true);
  };

  const handleCancelClick = (e) => {
    setTeamMemberData({ telephone: "" });
    handleCloseModal();
  };

  useEffect(() => {
    const loadMemberTypes = async () => {
      setConfigLoading(true);
      const payload = {
        configurationKey: ["niramaya.patient.carecircle.memberType"],
      };
      const response = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload
      );
      const result = JSON.parse(response[0]?.configurationValue || "[]");
      const types = result?.map((value) => ({
        value: value,
        label: value,
      }));
      setMemberTypes(types);
      setConfigLoading(false);
    };

    loadMemberTypes();
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmit) {
      addTeamMember();
    }
  }, [errors]);

  const customModelButtons = (
    <>
      <SecondaryButton buttonText="Cancel" handleOnClick={handleCancelClick} />
      <PrimaryButton
        buttonText="Add"
        handleOnClick={handleModalPrimaryButtonOnclick}
        loading={isLoading}
      />
    </>
  );

  return (
    <>
      <CustomModal
        closable={false}
        isModalOpen={isModalOpen}
        footer={customModelButtons}
        closeModal={setIsModalOpen}
        title="Add Care Circle Member"
      >
        {configLoading ? (
          <Spin size="medium" className="spinnerCenter" />
        ) : (
          <>
            <FormControl fullWidth>
              <Grid container spacing={2}>
                <Grid xs={12} item>
                  <FormControl
                    required
                    error={errors?.memberType}
                    sx={{ width: "100%" }}
                    className="add-edit-select"
                  >
                    <InputLabel id="member-type-label" required>
                      Member Type
                    </InputLabel>
                    <Select
                      labelId="member-type-label"
                      label="Member Type"
                      required
                      onChange={handleSelectMemberType}
                      MenuProps={MenuProps}
                      error={errors?.memberType}
                      input={<CustomOutlinedInput label="Member Type" />}
                    >
                      {memberTypes?.map((item) => (
                        <MenuItem value={item?.value} key={item?.value}>
                          {item?.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors?.memberType && (
                      <FormHelperText>{errors?.memberType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    required
                    fullWidth
                    error={errors.givenName === undefined ? false : true}
                    value={teamMemberData?.givenName}
                    name="givenName"
                    label="First Name"
                    helperText={errors?.givenName}
                    variant="outlined"
                    onChange={handleChange}
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextField
                    required
                    fullWidth
                    error={errors.familyName === undefined ? false : true}
                    value={teamMemberData?.familyName}
                    name="familyName"
                    label="Family Name"
                    variant="outlined"
                    helperText={errors?.familyName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid xs={12} item>
                  <TextField
                    type="email"
                    required
                    error={errors.email === undefined ? false : true}
                    value={teamMemberData?.email}
                    fullWidth
                    name="email"
                    label="Email"
                    helperText={errors?.email}
                    variant="outlined"
                    onChange={handleChange}
                  />
                </Grid>
                <Grid xs={12} item>
                  <MuiTelInput
                    fullWidth
                    forceCallingCode
                    defaultCountry="US"
                    value={teamMemberData?.telephone}
                    onChange={handlePhoneNumberChange}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </>
        )}
      </CustomModal>
    </>
  );
};

export default AddEditTeamMemberModalComponent;
